import { useEffect, useId } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Input from 'components/Input';
import Select from 'components/Select';
import { requestRestreaming } from 'features/restreamer/actions';
import { useTranslation } from 'react-i18next';
import { closeModal } from 'features/modal/modalSlice';
import { Dialog, DialogTitle } from 'components/Dialog';
import { RestreamingConfig } from 'features/restreamer/types';
import { selectRestreamingDefaultConfig } from 'features/restreamer/restreamerSlice';
import { useRestreamer } from 'features/restreamer/useRestreamer';

const restreamingConfigSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.enum(['youtube', 'cloudflare', 'vimeo']),
    streamKey: z.string().min(1, 'Stream key is required'),
  }),
  z.object({
    type: z.literal('custom'),
    serverUrl: z.url('Please enter a valid RTMP URL').min(1, 'Stream URL is required'),
    streamKey: z.string().min(1, 'Stream key is required'),
  }),
]);

type RestreamingConfigFormValues = z.infer<typeof restreamingConfigSchema>;

const getDefaultValues = (defaultConfig?: RestreamingConfig): RestreamingConfigFormValues => {
  if (defaultConfig?.type === 'custom' && 'serverUrl' in defaultConfig) {
    return {
      type: defaultConfig.type,
      streamKey: defaultConfig.streamKey,
      serverUrl: defaultConfig.serverUrl,
    };
  }
  return {
    type: (defaultConfig?.type || 'youtube') as RestreamingConfigFormValues['type'],
    streamKey: defaultConfig?.streamKey || '',
    serverUrl: '',
  };
};

const RestreamingConfigModal = () => {
  const { t } = useTranslation('modals');

  const dispatch = useAppDispatch();
  const modalId = useId();

  const { isFeatureAvailable } = useRestreamer();

  const defaultConfig = useAppSelector(selectRestreamingDefaultConfig);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<RestreamingConfigFormValues>({
    resolver: zodResolver(restreamingConfigSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues(defaultConfig),
  });

  const typeValue = watch('type');

  const modalShouldBeClosed = !isFeatureAvailable;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('restreamingConfig'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const onSubmit: SubmitHandler<RestreamingConfigFormValues> = (data) => {
    dispatch(requestRestreaming(data as RestreamingConfig));
    dispatch(closeModal('restreamingConfig'));
  };

  const handleCancel = () => {
    dispatch(closeModal('restreamingConfig'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      aria-describedby={`${modalId}-description`}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle id={`${modalId}-title`}>{t('restreaming.config.title')}</DialogTitle>
        <DialogContent>
          <Stack
            spacing={6}
            sx={{
              pt: 1,
            }}
          >
            <FormControl fullWidth error={!!errors?.type}>
              <Controller
                name="type"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <Select
                    {...field}
                    inputRef={ref}
                    inputProps={{
                      'aria-label': t('restreaming.config.platform_aria_label'),
                    }}
                  >
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="cloudflare">Cloudflare</MenuItem>
                    <MenuItem value="vimeo">Vimeo</MenuItem>
                    <MenuItem value="custom">{t('restreaming.config.platforms.custom')}</MenuItem>
                  </Select>
                )}
              />
            </FormControl>

            {typeValue === 'custom' && (
              <Controller
                name="serverUrl"
                control={control}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <Input
                      {...field}
                      inputRef={ref}
                      id={`${modalId}-url`}
                      placeholder={t('restreaming.config.url_placeholder')}
                      aria-label={t('restreaming.config.url_aria_label')}
                    />
                    <FormHelperText>{t('restreaming.config.url_help_text')}</FormHelperText>
                  </FormControl>
                )}
              />
            )}

            <FormControl fullWidth error={!!errors?.streamKey}>
              <Controller
                name="streamKey"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <Input
                    {...field}
                    inputRef={ref}
                    id={`${modalId}-streamKey`}
                    placeholder={t('restreaming.config.stream_key_placeholder')}
                    aria-label={t('restreaming.config.stream_key_aria_label')}
                  />
                )}
              />
              <FormHelperText>{t('restreaming.config.stream_key_help_text')}</FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="grayscale">
            {t('restreaming.config.btn_cancel_label')}
          </Button>
          <Button type="submit" disabled={!isValid}>
            {t('restreaming.config.btn_ok_label')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RestreamingConfigModal;
